import React, { useCallback, useState, useEffect } from 'react';
import {
  Checkbox,
  Box,
  IconButton,
  Slider,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import OpacityIcon from '@mui/icons-material/Opacity';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { Legend } from './Legend';
import { useContext } from './context';
import { LayerButton } from './LayerButton';

export function DesktopLayers() {
  const {
    layers,
    isOpen,
    setOpen,
    visibleLegends,
    visibleOpacities,
    toggleLegend,
    toggleOpacity,
    changeOpacity,
    buildLayerName,
  } = useContext();

  const {
    toggle: toggleLayer,
    isLoading,
    isHidden,
    isDisabled,
    isVisible,
    updateZIndex,
  } = layers;

  const [order, setOrder] = useState(layers.map((l) => l.id));

  const onDragEnd = useCallback(({ destination, source }) => {
    if (destination && source) {
      setOrder((current) => {
        const layerId = current[source.index];
        current.splice(source.index, 1);
        current.splice(destination.index, 0, layerId);
        return [...current];
      });
    }
  }, []);

  useEffect(() => {
    layers
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
      .forEach((layer, index) => updateZIndex(layer, index));
  }, [layers, order, updateZIndex]);

  return (
    <Box
      sx={{
        m: 1,
        position: 'relative',
      }}
    >
      <LayerButton sx={{ width: 36 }} />
      <Box
        className="layer-list"
        sx={{
          display: isOpen ? 'flex' : 'none',
          flexDirection: 'column',
          minWidth: 350,
          bgcolor: 'white',
          pointerEvents: 'auto',
          position: 'absolute',
          right: 0,
          maxHeight: 'calc(100% - 60px)',
        }}
      >
        {buildHeader()}
        {buildLayersContainer()}
      </Box>
    </Box>
  );

  function buildHeader() {
    return (
      <Box
        sx={{
          fontSize: 15,
          bgcolor: 'black',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, pl: 1.5 }}>Map Layers</Box>
        <IconButton title="Close" onClick={() => setOpen(!isOpen)}>
          <CloseIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>
    );
  }

  function buildLayersContainer() {
    return (
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provider) => (
              <div {...provider.droppableProps} ref={provider.innerRef}>
                {buildLayers()}
                {provider.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  }

  function buildLayers() {
    return layers
      .filter((layer) => !isHidden(layer))
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
      .map((layer, index) => (
        <Draggable key={layer.id} draggableId={layer.id} index={index}>
          {(provider) => (
            <div
              ref={provider.innerRef}
              {...provider.draggableProps}
              {...provider.dragHandleProps}
            >
              {buildLayer(layer, index, provider)}
            </div>
          )}
        </Draggable>
      ));
  }

  function buildLayer(layer, i, provider) {
    const isLayerVisible = isVisible(layer);
    const isLegendVisible = visibleLegends.includes(layer.id);

    return (
      <Box key={i} data-testid="map-layer">
        <Box
          sx={{
            fontSize: 15,
            display: 'flex',
            alignItems: 'center',
            py: 0,
          }}
        >
          {buildLayerCheckbox(layer)}
          {buildActions(layer, provider)}
        </Box>

        {isLayerVisible && isLegendVisible && <Legend layer={layer} />}
      </Box>
    );
  }

  function buildLayerCheckbox(layer) {
    const isLayerVisible = isVisible(layer);
    const isLayerLoading = isLoading(layer);
    const isLayerDisabled = isDisabled(layer);
    const showSpinner = isLayerLoading;

    const getCheckboxColor = () => {
      if (layer.error) return 'error';
      if (isLayerDisabled) return 'primary';
      return 'default';
    };

    const checkbox = showSpinner ? (
      <CircularProgress color="inherit" sx={{ m: 1.5 }} size={18} />
    ) : (
      <Checkbox
        color={getCheckboxColor()}
        inputProps={{ 'data-testid': `layer-${layer.id}-visibility` }}
        checked={isLayerVisible}
        onChange={() => toggleLayer(layer)}
      />
    );

    return (
      <FormControlLabel
        disabled={isLayerDisabled}
        sx={{ flex: 1, pl: 1.5, pr: 2.5, cursor: 'grab' }}
        control={checkbox}
        label={buildLayerName(layer)}
      />
    );
  }

  function buildOpacityAction(layer, dragProvider) {
    if (layer.opacityControl && isVisible(layer) && !isDisabled(layer)) {
      return (
        <Box style={{ position: 'relative' }}>
          <IconButton
            title="Opacity"
            sx={{ py: 0, position: 'absolute', right: 0 }}
            onClick={() => toggleOpacity(layer)}
          >
            <OpacityIcon />
          </IconButton>
          {visibleOpacities.includes(layer.id) && (
            <Box
              data-rbd-drag-handle-context-id={
                dragProvider.dragHandleProps?.[
                  'data-rbd-drag-handle-context-id'
                ]
              }
              data-rbd-drag-handle-draggable-id="ignore-drag-and-drop"
              sx={{
                cursor: 'auto',
                position: 'absolute',
                bgcolor: 'white',
                right: 0,
                width: 100,
                pr: '16px',
              }}
            >
              <Slider
                color="primary"
                defaultValue={layer.dataLayer.style.fillOpacity * 100}
                onChange={(event) => changeOpacity(layer, event.target.value)}
                onChangeCommitted={() => toggleOpacity(layer)}
              />
            </Box>
          )}
        </Box>
      );
    }
  }

  function buildLegendAction(layer) {
    if (layer.legend && isVisible(layer) && !isDisabled(layer)) {
      return (
        <IconButton title="Legend" onClick={() => toggleLegend(layer)}>
          <FormatListBulletedIcon />
        </IconButton>
      );
    }
  }

  function buildActions(layer, dragProvider) {
    return (
      <Box
        sx={{
          minWidth: 34,
          display: 'flex',
          '& button': { py: 0, px: 0.5 },
        }}
      >
        {buildOpacityAction(layer, dragProvider)}
        {buildLegendAction(layer)}
      </Box>
    );
  }
}
